import {
  WittyAutocompleteApiRequest,
  WittyAutocompleteApiResponse,
} from '@agilelab/plugin-wb-witty-common';
import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { processTableFormData } from '../utils';

export type WittyAutocompleteApi = {
  autocomplete(
    request: WittyAutocompleteApiRequest,
  ): Promise<WittyAutocompleteApiResponse>;
};

export class WittyAutocompleteClient implements WittyAutocompleteApi {
  private dataProductInfoCache = new Map<string, any>();

  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async getDataProductInfo(
    catalogBaseUrl: string,
    dataProductName: string,
  ): Promise<any> {
    const cacheKey = `${catalogBaseUrl}/entities/by-name/system/default/${dataProductName}`;

    if (this.dataProductInfoCache.has(cacheKey)) {
      return this.dataProductInfoCache.get(cacheKey);
    }

    const dataProductInfoResponse = await this.fetchApi.fetch(cacheKey);
    const dataProductInfo = await dataProductInfoResponse.json();

    this.dataProductInfoCache.set(cacheKey, dataProductInfo);
    return dataProductInfo;
  }

  async autocomplete({
    tablePath,
    formData,
    optionsSets,
    suggestFields,
    additionalContext,
    selectRowIndex,
  }: WittyAutocompleteApiRequest): Promise<WittyAutocompleteApiResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');

    // we get a formData that contains only the data of the table (only the selected row if specified) with the original path structure intact
    const processedFormData = processTableFormData(formData, tablePath, {
      selectRowIndex,
    });

    const catalogBaseUrl = await this.discoveryApi.getBaseUrl('catalog');

    const dataProductName = formData.dataproduct.replace('system:', '');
    const dataProductInfo = await this.getDataProductInfo(
      catalogBaseUrl,
      dataProductName,
    );

    const enrichedFormData = {
      ...processedFormData,
      name: dataProductInfo.metadata.name,
      description: dataProductInfo.metadata.description,
    };

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/autocomplete/table`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          tablePath,
          formData: enrichedFormData,
          suggestFields,
          optionsSets,
          additionalContext,
        }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }
}

export const wittyAutocompleteApiRef: ApiRef<WittyAutocompleteApi> =
  createApiRef<WittyAutocompleteApi>({
    id: 'wb-witty-autocomplete',
  });
